import { useOrdersStoreDefinition } from '~/store/orders';

/**
 * Mirrors the in-component `beforeRouteEnter` guard that lived on
 * pages/_tenant/event/_id/confirm.vue before its <script setup> migration.
 *
 * The confirm page injects Protect Group's "Pulse" insurance widget via a
 * third-party hybrid <script> that must land in a fresh document, so when the
 * user reaches /confirm by SPA navigation from another page we force a full
 * browser load instead.
 *
 * In a Nuxt 2 middleware `route` is the navigation target (the guard's `to`)
 * and `from` is the previous route. The extra `from` check that the old
 * `beforeRouteEnter` didn't need: a page middleware also runs on this page's
 * own `router.replace({ query })` calls (stripping order_id / payment_type /
 * status), where `from` is also the confirm route — excluding those keeps it
 * from reload-looping. Net behaviour matches `beforeRouteEnter`: reload only
 * when arriving at /confirm from a different page. Fresh loads (no `from`)
 * pass straight through.
 */
export default function ({ route, from, app }) {
  const enteringConfirm = route.name?.startsWith('tenant-event-id-confirm___');
  const cameFromAnotherPage =
    from?.name && !from.name.startsWith('tenant-event-id-confirm___');

  if (process.client && enteringConfirm && cameFromAnotherPage) {
    // The browser load below discards the in-memory pinia store entirely.
    // pinia-plugin-persistedstate persists via Pinia's $subscribe, which
    // (unlike Vuex's synchronous store.subscribe(), which vuex-persistedstate
    // relied on) uses a deferred flush by default — a mutation made moments
    // earlier (e.g. holders.vue's addHolder calls, right before navigating
    // here) isn't guaranteed to have reached localStorage yet. Force a
    // synchronous flush of the one store this transition depends on before
    // the reload below wipes it.
    useOrdersStoreDefinition(app.pinia).$persist();
    window.location.href = route.fullPath;
    // Halt the SPA transition so the confirm component never mounts — the
    // browser load above takes over. (`beforeRouteEnter` achieved this by
    // never calling `next()`.) Without this, Nuxt finishes the client-side
    // navigation, renders /confirm for a beat, then the reload fires.
    return new Promise(() => {});
  }
}
